import { useEffect, useRef, useState } from 'react';
import { useDrop } from 'react-dnd';
import { useApolloClient } from '@apollo/client';

import type { InteractiveLessonSectionFragment } from '../lesson-builder.graphql.generated';
import type { InteractiveLessonSectionComponentFragment } from '../lesson-builder.graphql.generated';
import { ACTIVITIES_COMPONENT_TYPES } from 'global/constants';
import { COLUMN_COMPONENT_TYPES } from '../components/component.constants';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import {
  useComponentMutations,
  useGetInteractiveLessonDataFromCache,
} from '../activity-builder.hooks';
import { useBatchComponentUpdateContext } from '../components/shared/batch-component-update.context';
import {
  getScrollPosition,
  moveBetweenColumns,
  moveInsideColumn,
  moveOutsideColumn,
  setScrollPosition,
} from './droppable.utils';
import { getNewSortValue } from '../activity-builder.utils';
import { resetSectionInCache } from '../activity-builder.apollo.utils';
import { ColumnData, DroppableComponentData } from './droppable.constants';
import * as Styled from './draggable.style';

type Props = {
  previousComponentId?: string;
  nextComponentId?: string;
  column?: ColumnData;
  sectionId: string;
  sortedComponents: InteractiveLessonSectionComponentFragment[];
  isFixedPosition?: boolean;
  label?: string;
};

export const Droppable = ({
  previousComponentId,
  nextComponentId,
  column,
  sectionId,
  sortedComponents,
  isFixedPosition,
  label,
}: Props) => {
  const client = useApolloClient();
  const ref = useRef<HTMLDivElement | null>(null);

  const [canDropDelayed, setCanDropDelayed] = useState<boolean>(false);

  const { showError } = useSnackbar();

  const { updateComponentProperties } = useBatchComponentUpdateContext();

  const { getSection } = useGetInteractiveLessonDataFromCache();

  const { handleAdd } = useComponentMutations({
    sectionId,
    components: sortedComponents,
  });

  const handleUpdate = ({
    section,
    column,
    componentData,
    sortValue,
  }: {
    section: InteractiveLessonSectionFragment | undefined;
    column?: ColumnData;
    componentData: DroppableComponentData;
    sortValue: string;
  }) => {
    updateComponentProperties({
      input: {
        sectionId,
        parentDetails: column
          ? {
              parentComponentId: column.componentId,
              columnId: column.id,
            }
          : null,
        componentId: componentData.id,
        componentData: {
          sort: sortValue,
        },
      },
      typename: componentData.typename,
      onError: () => {
        if (section) {
          resetSectionInCache({
            client,
            section,
          });
        }
        showError('There was an error saving the content. Please try again');
      },
    });
  };

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: column ? COLUMN_COMPONENT_TYPES : ACTIVITIES_COMPONENT_TYPES,
      drop(componentData: DroppableComponentData) {
        const scrollPosition = getScrollPosition();
        const section = getSection(sectionId);

        if (componentData.menuItem) {
          // this catches item dropped from the component menu
          const payload = componentData.menuItem?.getPayload();
          handleAdd({
            ...payload,
            columnComponentId: column?.componentId,
            columnId: column?.id,
            sort: getNewSortValue({
              components: sortedComponents,
              componentId: previousComponentId,
            }),
          });
        } else if (!column && componentData.column) {
          const newSortValue = getNewSortValue({
            components: sortedComponents,
            componentId: previousComponentId,
          });
          moveOutsideColumn(client, section, newSortValue, componentData);
          handleUpdate({
            section,
            componentData,
            sortValue: newSortValue,
          });
        } else if (
          column &&
          componentData.column &&
          column?.id !== componentData.column?.id
        ) {
          // move between columns
          const newSortValue = getNewSortValue({
            components: sortedComponents,
            componentId: previousComponentId,
          });
          moveBetweenColumns(
            client,
            getSection(sectionId),
            newSortValue,
            {
              id: componentData.id,
              column: componentData.column,
            },
            column
          );
          handleUpdate({
            section,
            column,
            componentData,
            sortValue: newSortValue,
          });
        } else if (column && !componentData.column) {
          // move inside a column
          const newSortValue = getNewSortValue({
            components: sortedComponents,
            componentId: previousComponentId,
          });
          moveInsideColumn(
            client,
            getSection(sectionId),
            newSortValue,
            componentData,
            column
          );
          handleUpdate({
            section,
            column,
            componentData,
            sortValue: newSortValue,
          });
        } else {
          // reorder
          handleUpdate({
            section,
            column,
            componentData,
            sortValue: getNewSortValue({
              components: sortedComponents,
              componentId: previousComponentId,
            }),
          });
        }
        setScrollPosition(scrollPosition);
      },
      collect: monitor => {
        return {
          canDrop: monitor.canDrop(),
          isOver: monitor.isOver(),
          isOverCurrent: monitor.isOver({ shallow: true }),
        };
      },
      canDrop: (item: DroppableComponentData, monitor) => {
        // if the item is a column component dont show droppables inside columns
        if (item.isColumnComponent && column) {
          return false;
        }

        if (item.id === previousComponentId) {
          return false;
        }

        if (item.id === nextComponentId) {
          return false;
        }

        return true;
      },
    }),
    []
  );

  useEffect(() => {
    setTimeout(() => setCanDropDelayed(canDrop));
  }, [canDrop]);

  drop(ref);
  return (
    <Styled.DroppableWrapper
      $isDragging={!!canDrop}
      $isFixedPosition={isFixedPosition}
    >
      <Styled.Droppable ref={ref} $canDrop={canDropDelayed} $isOver={isOver}>
        {label ? label : 'Drop here'}
      </Styled.Droppable>
    </Styled.DroppableWrapper>
  );
};
